import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Container,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Paper,
  Radio,
  FormControlLabel,
  Checkbox,
  TextField
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import api from '../../../services/api';

const ViewSpecificResponse = () => {
  const { userId, formId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [formTemplate, setFormTemplate] = useState(null);
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const responseData = await api.get(`form-responses/response/${userId}/${formId}`);
        setResponse(responseData.data);
        setFormTemplate(responseData.data.formTemplate);
        setError(null);
      } catch (err) {
        setError('Erro ao carregar os dados da resposta');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, formId]);

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={isMobile ? 30 : 40} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
          variant="outlined"
        >
          Voltar
        </Button>
      </Container>
    );
  }

  if (!formTemplate || !response) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="warning">Formulário ou resposta não encontrados.</Alert>
        <Button
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
          variant="outlined"
        >
          Voltar
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth={isMobile ? 'xs' : 'md'} sx={{ mt: isMobile ? 2 : 0, mb: 4 }}>
      <Typography variant={isMobile ? 'h6' : 'h5'} align="center" sx={{ color: '#10641c', fontWeight: 'bold', mb: 3 }}>
        Detalhes da Resposta
      </Typography>

      <Paper sx={{ p: isMobile ? 2 : 3, mb: 3, borderRadius: 2, borderLeft: '4px solid #10641c', bgcolor: '#fff' }}>
        <Typography variant={isMobile ? 'h6' : 'h5'} gutterBottom sx={{ color: '#10641c', fontWeight: 'bold' }}>
          {formTemplate.name}
        </Typography>
        {formTemplate.description && (
          <Typography variant="body1" color="text.secondary">
            {formTemplate.description}
          </Typography>
        )}
        <Typography variant="body2" sx={{ mt: 2, color: '#000000' }}>
          Empreendedor: {response.answerer.name}
        </Typography>
        <Typography variant="body2" sx={{ color: '#000000' }}>
          Data/Hora: {new Date(response.dateAnswered).toLocaleString('pt-BR')}
        </Typography>
      </Paper>

      {formTemplate.sections.map((section) => (
        <Paper key={section.id} sx={{ p: isMobile ? 2 : 3, mb: 3, borderRadius: 2, borderLeft: '4px solid #10641c', bgcolor: '#fff' }}>
          <Typography variant="h6" sx={{ color: '#10641c', fontWeight: 'bold', mb: 2 }}>
            Seção {section.order}: {section.title}
          </Typography>
          {section.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {section.description}
            </Typography>
          )}

          {section.fields.sort((a, b) => a.order - b.order).map((field) => {
            const responseValue = field.response?.[0]?.FormFieldResponse?.value?.response ?? 'N/A';
            const checkboxValues = field.type === 'checkbox' && Array.isArray(responseValue) ? responseValue : [];
            const textFieldTypes = ['text', 'number'];

            return (
              <Box key={field.id} sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 'medium', color: '#000000' }}>
                  {field.label || 'Campo desconhecido'}{' '}
                  <span style={{ color: field.mandatory ? 'red' : 'green' }}>
                    {field.mandatory ? '(Obrigatório)' : '(Opcional)'}
                  </span>
                </Typography>


                {field.type === 'radio' && field.options ? (
                  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column' }}>
                    {field.options.map((option, optIndex) => (
                      <FormControlLabel
                        key={optIndex}
                        control={<Radio checked={responseValue === option} disabled sx={{ color: '#10641c', '&.Mui-checked': { color: '#10641c' } }} />}
                        label={<Typography variant="body2" sx={{ color: responseValue === option ? '#10641c' : 'text.secondary', fontWeight: responseValue === option ? 'bold' : 'normal' }}>{option}</Typography>}
                      />
                    ))}
                  </Box>
                ) : field.type === 'checkbox' && field.options ? (
                  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column' }}>
                    {field.options.map((option, optIndex) => {
                      const isChecked = checkboxValues.includes(option);
                      return (
                        <FormControlLabel
                          key={optIndex}
                          control={<Checkbox checked={isChecked} disabled sx={{ color: '#10641c', '&.Mui-checked': { color: '#10641c' } }} />}
                          label={<Typography variant="body2" sx={{ color: isChecked ? '#10641c' : 'text.secondary', fontWeight: isChecked ? 'bold' : 'normal' }}>{option}</Typography>}
                        />
                      );
                    })}
                  </Box>
                ) : field.type === 'textarea' || textFieldTypes.includes(field.type) ? (
                  <TextField
                    value={responseValue}
                    multiline={field.type === 'textarea'}
                    rows={field.type === 'textarea' ? 3 : 1}
                    fullWidth
                    variant="outlined"
                    disabled
                    sx={{ mt: 1, '& .MuiInputBase-input.Mui-disabled': { color: 'text.secondary', WebkitTextFillColor: 'text.secondary' }, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#10641c' } } }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {responseValue}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Paper>
      ))}

      {/*
      <Box sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem', borderColor: '#10641c', color: '#10641c', padding: isMobile ? '6px 12px' : '8px 16px', borderRadius: '8px', transition: 'all 0.3s ease-in-out', '&:hover': { borderColor: '#0a3d0f', color: '#0a3d0f', backgroundColor: '#e8f5e9', transform: 'scale(1.05)' } }}
        >
          Voltar
        </Button>
      </Box>
*/}
    </Container>
  );
};

export default ViewSpecificResponse;